type RuntimeSecretRequirementContext = 'backtest' | 'deploy' | 'start';

type RequirementItem = {
  key?: string;
  name?: string;
  envVar?: string;
  envVarName?: string;
  title?: string;
  label?: string;
  description?: string;
};

const normalizeKey = (value: unknown): string =>
  typeof value === 'string' ? value.trim().toUpperCase() : '';

const keyFromItem = (item: unknown): string => {
  if (typeof item === 'string') return normalizeKey(item);
  if (!item || typeof item !== 'object' || Array.isArray(item)) return '';
  const value = item as RequirementItem;
  return normalizeKey(value.key ?? value.name ?? value.envVar ?? value.envVarName);
};

const listItems = (input: unknown): Record<string, unknown>[] => {
  if (!input) return [];
  if (Array.isArray(input)) return input.filter(Boolean) as Record<string, unknown>[];
  if (typeof input !== 'object') return [];
  const source = input as Record<string, unknown>;
  return Array.isArray(source.requirements)
    ? source.requirements.filter(Boolean) as Record<string, unknown>[]
    : [source];
};

const appliesTo = (requirement: Record<string, unknown>, context: string): boolean => {
  const raw = requirement.requiredFor ?? requirement.contexts ?? requirement.required_for;
  if (!raw) return true;
  const values = (Array.isArray(raw) ? raw : [raw])
    .map((item) => String(item ?? '').trim().toLowerCase())
    .filter(Boolean);
  if (values.length === 0 || values.includes(context)) return true;
  return context === 'deploy' && values.includes('start');
};

const normalizedItem = (
  item: unknown,
  parent: Record<string, unknown>,
): { key: string; title: string; description: string } | null => {
  const key = keyFromItem(item);
  if (!key) return null;
  const source = item && typeof item === 'object' && !Array.isArray(item)
    ? item as RequirementItem
    : {};
  const title = String(source.title ?? source.label ?? parent.title ?? parent.label ?? key).trim();
  const description = String(source.description ?? parent.description ?? '').trim();
  return { key, title: title || key, description };
};

export function runtimeSecretRequirementItems(
  input: unknown,
  context: RuntimeSecretRequirementContext,
): Array<{ key: string; title: string; description: string }> {
  const items: Array<{ key: string; title: string; description: string }> = [];
  const seen = new Set<string>();
  for (const requirement of listItems(input)) {
    if (!appliesTo(requirement, context)) continue;
    const allOf = [
      ...(Array.isArray(requirement.allOf) ? requirement.allOf : []),
      ...(Array.isArray(requirement.keys) ? requirement.keys : []),
      requirement,
    ];
    for (const item of allOf) {
      const normalized = normalizedItem(item, requirement);
      if (!normalized || seen.has(normalized.key)) continue;
      seen.add(normalized.key);
      items.push(normalized);
    }
    const rawAnyOf = Array.isArray(requirement.anyOf) ? requirement.anyOf : [];
    const groups = Array.isArray(rawAnyOf[0]) ? rawAnyOf : rawAnyOf.length > 0 ? [rawAnyOf] : [];
    for (const group of groups) {
      for (const item of group as unknown[]) {
        const normalized = normalizedItem(item, requirement);
        if (!normalized || seen.has(normalized.key)) continue;
        seen.add(normalized.key);
        items.push(normalized);
      }
    }
  }
  return items;
}

export function evaluateRuntimeSecretRequirements(
  input: unknown,
  availableKeys: string[],
  context: RuntimeSecretRequirementContext,
  options: { metadataStatus?: 'known' | 'unknown' } = {},
) {
  const available = new Set((availableKeys ?? []).map(normalizeKey).filter(Boolean));
  const required = new Set<string>();
  const present = new Set<string>();
  const missing: Array<{ key: string; title: string; description: string; message: string }> = [];
  for (const requirement of listItems(input)) {
    if (!appliesTo(requirement, context)) continue;
    const allOf = [
      ...(Array.isArray(requirement.allOf) ? requirement.allOf : []),
      ...(Array.isArray(requirement.keys) ? requirement.keys : []),
      requirement,
    ];
    for (const item of allOf) {
      const normalized = normalizedItem(item, requirement);
      if (!normalized) continue;
      required.add(normalized.key);
      if (available.has(normalized.key)) present.add(normalized.key);
      else missing.push({
        ...normalized,
        message: normalized.description || `${normalized.title} is required for this strategy.`,
      });
    }
    const rawAnyOf = Array.isArray(requirement.anyOf) ? requirement.anyOf : [];
    const groups = Array.isArray(rawAnyOf[0]) ? rawAnyOf : rawAnyOf.length > 0 ? [rawAnyOf] : [];
    for (const group of groups) {
      const normalizedGroup = (group as unknown[])
        .map((item) => normalizedItem(item, requirement))
        .filter(Boolean) as Array<{ key: string; title: string; description: string }>;
      normalizedGroup.forEach((item) => required.add(item.key));
      const matched = normalizedGroup.filter((item) => available.has(item.key));
      if (matched.length > 0) {
        matched.forEach((item) => present.add(item.key));
      } else if (normalizedGroup.length > 0) {
        missing.push({
          key: normalizedGroup.map((item) => item.key).join('|'),
          title: normalizedGroup.map((item) => item.title).join(' or '),
          description: '',
          message: `Add one of: ${normalizedGroup.map((item) => item.title).join(', ')}.`,
        });
      }
    }
  }
  const metadataStatus = options.metadataStatus ?? (input == null ? 'unknown' : 'known');
  return {
    required: Array.from(required),
    present: Array.from(present),
    missing,
    metadataStatus,
    ready: metadataStatus === 'known' && missing.length === 0,
  };
}

type CompatibilityActionStatus = {
  status?: string;
  reason?: string | null;
  source?: string | null;
  checkedAt?: string | null;
  missingRuntimeSecretKeys?: string[];
  [key: string]: unknown;
};

type CompatibilityRequirement = {
  runtimeSecretKeys?: string[];
  statusByAction?: Record<string, CompatibilityActionStatus | undefined>;
  [key: string]: unknown;
};

type CompatibilityAssessment = {
  requirements?: CompatibilityRequirement[];
  [key: string]: unknown;
};

export function resolveCompatibilitySetupWithAvailableKeys<T extends CompatibilityAssessment | null | undefined>(
  assessment: T,
  availableKeys: string[],
  action: string,
): T {
  if (!assessment || !Array.isArray(assessment.requirements)) return assessment;
  const available = new Set((availableKeys ?? []).map(normalizeKey).filter(Boolean));
  const requirements = assessment.requirements.map((requirement) => {
    const actionStatus = requirement.statusByAction?.[action];
    if (actionStatus?.status !== 'setup_required') return requirement;
    const requiredKeys = Array.from(new Set([
      ...(Array.isArray(requirement.runtimeSecretKeys) ? requirement.runtimeSecretKeys : []),
      ...(Array.isArray(actionStatus.missingRuntimeSecretKeys)
        ? actionStatus.missingRuntimeSecretKeys
        : []),
    ].map(normalizeKey).filter(Boolean)));
    if (requiredKeys.length === 0 || !requiredKeys.every((key) => available.has(key))) {
      return requirement;
    }
    return {
      ...requirement,
      statusByAction: {
        ...requirement.statusByAction,
        [action]: {
          ...actionStatus,
          status: 'available',
          reason: 'The selected saved access satisfies this prerequisite.',
          source: 'selected_saved_secret_key_metadata',
          checkedAt: new Date().toISOString(),
          missingRuntimeSecretKeys: [],
        },
      },
    };
  });
  return { ...assessment, requirements } as T;
}
